import { useCallback, useEffect, useRef, useState } from 'react';
import { Placement, Size } from '@Components/designConstants';
import { Dropdown } from '../../Components/Dropdown';
import { CheckOption, NestedDropdownProps, OnChange, OnChangeHelper } from './fiscalCalConstants';

export interface MultiDropDownQuestionProps {
  options: { value: string | number | boolean; label: string }[];
  checkOptions?: CheckOption;
  onChange: OnChange;
  formState: any;
  formPath: string;
  disabled?: boolean;
  onChangeHelper?: OnChangeHelper;
  dropdownProps?: NestedDropdownProps;
}

export const MultiDropDownQuestion = ({
  formState,
  checkOptions,
  formPath,
  options,
  onChange,
  onChangeHelper,
  dropdownProps,
  disabled = false
}: MultiDropDownQuestionProps) => {
  const [multiDropdownOpen, setMultiDropdownOpen] = useState(false);
  const isOpen = !disabled && multiDropdownOpen;
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && isOpen) {
        setMultiDropdownOpen(false);
      }
    },
    [isOpen]
  );
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event instanceof KeyboardEvent && event.key === 'Escape' && isOpen) {
        setMultiDropdownOpen(false);
      }
    },
    [isOpen]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleClickOutside, handleEscape]);

  const dropdownValue = (() => {
    if (disabled) return '—';
    if (checkOptions) return checkOptions();
    return formState[formPath];
  })();

  return (
    <div ref={dropdownRef}>
      <Dropdown
        overlayPlacement={Placement.RIGHT}
        size={Size.LG}
        value={dropdownValue}
        options={options}
        onChange={value => {
          onChange(value, formPath, onChangeHelper);
        }}
        {...dropdownProps}
        disabled={disabled}
        hasFilterBar={false}
        multiSelect
        hideCancelOnClick
        hideApplyOnClick
        onToggle={(_nextShow, meta) => {
          if (!isOpen) {
            setMultiDropdownOpen(true);
          } else if (meta.source === 'click') {
            setMultiDropdownOpen(false);
          }
        }}
        show={isOpen}
      />
    </div>
  );
};
